import { useEffect, useRef } from 'react';

import { ShieldCheck } from 'lucide-react';
import { Outlet, useLocation } from 'react-router-dom';

import { Box, Chip, Divider, Stack, useTheme } from '@mui/material';

import { BottomNavigation } from '~components/layout/bottom-navigation/bottom-navigation';
import { Header } from '~components/layout/header/header';
import { LessorSidebarFooter } from '~components/layout/sidebar/components/lessor-sidebar-footer';
import { SidebarItem } from '~components/layout/sidebar/components/sidebar-item';
import { SidebarWrapper } from '~components/layout/sidebar/sidebar.styles';
import { Logo } from '~components/ui/logo/logo';
import { AppTypography } from '~components/ui/typography/app-typography';

import { useMobileMenu } from '~hooks/use-mobile-menu';

import { ROUTES } from '~app/router/routes';

import { ReactComponent as IconBell } from '~assets/icons/icon-bell-ring.svg';
import { ReactComponent as IconBill } from '~assets/icons/icon-bill.svg';
import { ReactComponent as IconBuilding } from '~assets/icons/icon-building-1.svg';
import { ReactComponent as IconChart } from '~assets/icons/icon-chart-1.svg';
import { ReactComponent as IconContract } from '~assets/icons/icon-contract.svg';
import { ReactComponent as IconUsers } from '~assets/icons/icon-users.svg';
import { ReactComponent as IconWallet } from '~assets/icons/icon-wallet-money.svg';

const navItems = [
  {
    label: 'Користувачі',
    path: ROUTES.ADMIN_USERS,
    icon: IconUsers,
  },
  {
    label: 'Outreach',
    path: ROUTES.ADMIN_OUTREACH,
    icon: IconChart,
  },
  {
    label: 'Comm Events',
    path: ROUTES.ADMIN_COMM_EVENTS,
    icon: IconBell,
  },
  {
    label: 'Рахунки',
    path: ROUTES.ADMIN_INVOICES,
    icon: IconBill,
  },
  {
    label: 'Платежі',
    path: ROUTES.ADMIN_PAYMENTS,
    icon: IconWallet,
  },
  {
    label: 'Договори',
    path: ROUTES.ADMIN_LEASES,
    icon: IconContract,
  },
  {
    label: 'Нерухомість',
    path: ROUTES.ADMIN_PROPERTIES,
    icon: IconBuilding,
  },
  {
    label: 'Запити партнерства',
    path: ROUTES.ADMIN_PARTNER_APPLICATIONS,
    icon: IconUsers,
  },
];

export const AdminLayout = () => {
  const { isMenuOpen, closeMenu } = useMobileMenu();
  const { pathname } = useLocation();
  const prevPathname = useRef(pathname);
  const theme = useTheme();

  useEffect(() => {
    if (prevPathname.current !== pathname) {
      closeMenu();
      prevPathname.current = pathname;
    }
  }, [pathname, closeMenu]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'fog.95' }}>
      <Box sx={{ display: 'flex' }}>
        <SidebarWrapper
          isMenuOpen={isMenuOpen}
          sx={{
            backgroundColor: 'fog.10',
            color: 'fog.100',
            borderRight: `1px solid ${theme.palette.fog['25']}`,
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'center',
              mb: 3,
              filter: 'brightness(0) invert(1)',
            }}
          >
            <Logo />
          </Box>

          <Box
            sx={{
              border: `1px solid ${theme.palette.fog['25']}`,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              p: 1.5,
              mb: 2.5,
            }}
          >
            <Stack direction="row" alignItems="center" gap={1.25}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.5,
                  backgroundColor: 'primaryTokens.100',
                  color: 'common.white',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <ShieldCheck size={19} />
              </Box>

              <Box minWidth={0}>
                <AppTypography size="body-md" weight="bold" color="fog.100">
                  Admin mode
                </AppTypography>
                <AppTypography size="body-caption" color="fog.75">
                  Операційна зона
                </AppTypography>
              </Box>
            </Stack>
          </Box>

          <Divider sx={{ mb: 1.5, borderColor: 'fog.25' }} />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              pr: 0.5,
              mb: 0.5,
            }}
          >
            {navItems.map((item) => (
              <SidebarItem
                key={item.path}
                to={item.path}
                label={item.label}
                icon={item.icon}
                activeColor="common.white"
                inactiveColor="fog.65"
                activeBackgroundColor="rgba(255, 255, 255, 0.12)"
                hoverBackgroundColor="rgba(255, 255, 255, 0.08)"
              />
            ))}
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            <LessorSidebarFooter />
          </Box>
        </SidebarWrapper>

        <Box sx={{ width: { xs: '100%', md: 'calc(100% - 268px)' } }}>
          <Header />
          <Box
            sx={{
              mx: { xs: 1.5, sm: 3 },
              mt: { xs: 1.5, md: 0 },
              mb: { xs: 1, md: 0 },
              border: `1px solid ${theme.palette.primaryTokens['25']}`,
              backgroundColor: 'primaryTokens.10',
              borderRadius: 2,
              px: 2,
              py: 1.25,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box minWidth={0}>
              <AppTypography
                size="body-sm"
                weight="bold"
                color="fog.20"
                sx={{ display: 'block', overflowWrap: 'anywhere' }}
              >
                Адміністративний контекст
              </AppTypography>
              <AppTypography
                size="body-caption"
                color="fog.40"
                sx={{ display: 'block', overflowWrap: 'anywhere' }}
              >
                Дані переглядаються через admin API, не через lessor scope.
              </AppTypography>
            </Box>

            <Chip
              size="small"
              label="ADMIN"
              color="primary"
              sx={{
                fontWeight: 800,
                letterSpacing: 0,
                alignSelf: { xs: 'flex-start', sm: 'center' },
              }}
            />
          </Box>

          <Outlet />
        </Box>

        <BottomNavigation />
      </Box>

      <Box
        role={'button'}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          opacity: isMenuOpen ? 1 : 0,
          visibility: isMenuOpen ? 'visible' : 'hidden',
          pointerEvents: isMenuOpen ? 'auto' : 'none',
          transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
        }}
        onClick={closeMenu}
      />
    </Box>
  );
};
